import React, { useEffect, useRef, useState } from "react";
import styles from "./ColorElement.module.scss";
import clsx from "clsx";
import { PortalTooltip } from "../PortalTooltip/PortalTooltip";

interface IProps {
  active?: boolean;
  onClick?: () => void;
  img?: string;
  name?: string;
  wrapperRef?: React.RefObject<HTMLDivElement>;
}
export const ColorElement: React.FC<IProps> = props => {
  const [showTooltip, setShowTooltip] = useState(false);
  const colorTooltipRef = useRef<HTMLDivElement | null>(null);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [alignClass, setAlignClass] = useState<"" | "left" | "right">("");

  const {
    active = false,
    onClick,
    img = "https://2d-render-admin-storage.fra1.digitaloceanspaces.com/projects/4/options/9/variants/35/1734524419517-1728852207660-Black.png",
    name = "Unknown",
    wrapperRef,
  } = props;

  // After tooltip shows, deterministically choose alignment using anchor center
  // and measured tooltip width. Recheck after fonts load (width may change).
  useEffect(() => {
    if (!showTooltip) return;

    let raf = 0;
    let cancelled = false;

    const margin = 8;

    const measure = () => {
      const el = colorTooltipRef.current;
      const anchor = anchorRef.current;
      if (!el || !anchor) {
        raf = requestAnimationFrame(measure);
        return;
      }

      const anchorRect = anchor.getBoundingClientRect();
      const centerX = anchorRect.left + anchorRect.width / 2;
      const tooltipWidth = el.offsetWidth || el.getBoundingClientRect().width;

      const centerLeft = centerX - tooltipWidth / 2;
      const centerRight = centerX + tooltipWidth / 2;
      const viewportRight = window.innerWidth - margin;

      let nextAlign: "" | "left" | "right" = "";
      if (centerLeft < margin && centerRight > viewportRight) {
        // If both sides overflow, choose closer side to keep maximum visible
        const overflowLeft = margin - centerLeft;
        const overflowRight = centerRight - viewportRight;
        nextAlign = overflowRight >= overflowLeft ? "right" : "left";
      } else if (centerLeft < margin) {
        nextAlign = "left"; // shift tooltip to the right of center
      } else if (centerRight > viewportRight) {
        nextAlign = "right"; // shift tooltip fully to the left of center
      } else {
        nextAlign = ""; // centered fits
      }

      setAlignClass(nextAlign);

      // Re-check once fonts finish loading as width can change
      const anyDoc: any = document as any;
      if (anyDoc.fonts?.ready) {
        anyDoc.fonts.ready.then(() => {
          if (!cancelled) {
            const w = el.offsetWidth || el.getBoundingClientRect().width;
            const cLeft = centerX - w / 2;
            const cRight = centerX + w / 2;
            let align: "" | "left" | "right" = "";
            if (cLeft < margin && cRight > viewportRight) {
              const oL = margin - cLeft;
              const oR = cRight - viewportRight;
              align = oR >= oL ? "right" : "left";
            } else if (cLeft < margin) {
              align = "left";
            } else if (cRight > viewportRight) {
              align = "right";
            } else {
              align = "";
            }
            setAlignClass(align);
          }
        });
      }
    };

    raf = requestAnimationFrame(measure);

    return () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
    };
  }, [showTooltip]);

  return (
    <div
      role="button"
      className={clsx(styles.item, {
        [styles.item_name]: !img,
        [styles.active]: active,
      })}
      ref={anchorRef}
      onClick={onClick}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {!!img ? (
        <img src={img} alt="img_item" />
      ) : (
        <div className={styles.color_name}>{name}</div>
      )}

      <PortalTooltip anchorEl={anchorRef.current} open={showTooltip}>
        <div
          ref={colorTooltipRef}
          className={clsx(
            styles.tooltip,
            alignClass === "left" && styles.rgbLeft,
            alignClass === "right" && styles.rgbRight
          )}
          style={{ pointerEvents: "auto" }}
        >
          {name}
        </div>
      </PortalTooltip>
    </div>
  );
};
